import React from 'react';

import { Box, Typography } from '@mui/material';
import { PresenceSectionDot } from '@sharedCR/common/PresenceDot';
import { checkIsTabHidden } from 'app/pages/system/autoLockRules/AutoLockRules.utils';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { getConditionalArray } from 'utils/data';
import { formatNumberToCurrencyString } from 'utils/number';

import { openConfirmationDialogAction } from 'store/global/actions';

import {
  ALL_BORROWERS_LABEL,
  ALL_BORROWERS_PATH,
  FINANCIAL_INFO_VIEWS_PATHS,
  NEW_RECORD_DATA,
} from '../FinancialInfo.constants';
import { generateFinancialInfoPath } from '../FinancialInfo.utils';

import TabLabel from './TabLabel';
import {
  headTabLabelStyles,
  labelTextContentStyles,
  labelTextStyles,
} from './UpdateTableModal.styles';
import {
  getInvalidBorrowerIds,
  getRecordDisplayName,
  getRecordFlags,
  hasAnyValidationError,
} from './UpdateTableModal.utils';

const getTab = ({
  applicationId,
  tab,
  tablePath,
  actionType,
  recordId,
  borrowerId,
  label,
  pathFrom,
  value,
  history,
  isDeleted,
  isModified,
  isInvalid,
  pendingChangesCount,
  isNestedEntityEditActive,
  setIsNestedEntityEditActive,
  dispatch,
}) => ({
  pendingChangesCount,
  value,
  label,
  isDeleted,
  isModified,
  isInvalid,
  onClick: () => {
    const navigate = () =>
      history.push(
        generateFinancialInfoPath({
          applicationId,
          tab,
          tablePath,
          borrowerId,
          actionType,
          recordId,
          pathFrom,
        }),
      );

    if (isNestedEntityEditActive) {
      dispatch(
        openConfirmationDialogAction({
          type: ConfirmationModalType.WARNING,
          message: 'You are leaving this tab. Unsaved changes will be lost.',
          onAccept: () => {
            navigate();
            setIsNestedEntityEditActive(false);
          },
          onAcceptButtonName: 'Proceed',
          withButtons: true,
        }),
      );

      return;
    }

    navigate();
  },
});

export const getHeadTabs = ({
  applicationId,
  tab,
  tablePath,
  actionType,
  recordId,
  borrowers,
  isAllBorrowersTabVisible,
  pathFrom,
  history,
  pendingChangesCounts,
  newRecordsOwners,
  validationErrorSummary,
  setIsNestedEntityEditActive,
  isNestedEntityEditActive,
  dispatch,
}) => {
  const invalidBorrowerIds = getInvalidBorrowerIds(validationErrorSummary);

  return [
    ...getConditionalArray(isAllBorrowersTabVisible, () => [
      getTab({
        applicationId,
        tab,
        tablePath,
        actionType,
        recordId,
        borrowerId: ALL_BORROWERS_PATH,
        label: ALL_BORROWERS_LABEL,
        pathFrom,
        value: ALL_BORROWERS_PATH,
        history,
        pendingChangesCount: Object.entries(pendingChangesCounts).reduce((acc, [id, count]) => {
          if (id.startsWith(NEW_RECORD_DATA.id)) {
            return acc + (newRecordsOwners[id]?.length ? count : 0);
          }

          return acc + count;
        }, 0),
        isInvalid: hasAnyValidationError(validationErrorSummary),
        isNestedEntityEditActive,
        setIsNestedEntityEditActive,
        dispatch,
      }),
    ]),
    ...borrowers.map(({ id, fullName, modifiedFields, records }) => {
      const mergedRecordsWithNewRecords = Object.entries(newRecordsOwners).reduce(
        (acc, [newRecordId, owners]) => {
          if (owners.includes(id)) {
            acc.push({ id: newRecordId });
          }

          return acc;
        },
        [...records],
      );

      return getTab({
        applicationId,
        tab,
        tablePath,
        actionType,
        recordId,
        borrowerId: id,
        label: (
          <Box sx={headTabLabelStyles}>
            <PresenceSectionDot sectionId={id} subtab={tablePath} />
            {fullName}
          </Box>
        ),
        pathFrom,
        value: id,
        history,
        isModified: modifiedFields.includes('FullName'),
        isInvalid: invalidBorrowerIds.has(id),
        pendingChangesCount: mergedRecordsWithNewRecords.reduce(
          (acc, { id }) => acc + (pendingChangesCounts[id] ?? 0),
          0,
        ),
      });
    }),
  ];
};

const getNavigationLabelText = ({ tablePath, tabData }) => {
  const isNewRecord = tabData.id.startsWith(NEW_RECORD_DATA.id);
  const primaryLabelText = isNewRecord
    ? NEW_RECORD_DATA.name
    : getRecordDisplayName({ tablePath, record: tabData });
  const secondaryLabelText =
    !isNewRecord && tablePath === FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT
      ? formatNumberToCurrencyString(tabData.monthlyIncome)
      : null;

  return (
    <Box sx={labelTextContentStyles}>
      <Typography
        data-truncated
        className="label-text truncate"
        sx={labelTextStyles}
        variant="regular14"
      >
        {primaryLabelText}
      </Typography>
      {!!secondaryLabelText && (
        <Typography
          data-truncated
          className="label-text truncate"
          sx={labelTextStyles}
          variant="regular14"
        >
          {secondaryLabelText}
        </Typography>
      )}
    </Box>
  );
};

export const getNavigationTabs = ({
  applicationId,
  tab,
  tablePath,
  borrowerId,
  actionType,
  data,
  pathFrom,
  history,
  onDeleteRecord,
  onRestoreRecord,
  deletedRecords,
  deletedInitialRecords,
  newAddedInitialRecords,
  pendingChangesCounts,
  validationErrorSummary,
  setIsNestedEntityEditActive,
  isNestedEntityEditActive,
  dispatch,
  hiddenPageIds,
  hiddenFieldIds,
}) =>
  data.reduce((acc, tabData) => {
    const { id, isSubjectProperty } = tabData;

    if (checkIsTabHidden(id, hiddenPageIds, hiddenFieldIds)) {
      return acc;
    }

    const { isDeletedByCurrentSession, isDeletedByInit } = getRecordFlags({
      id,
      deletedRecords,
      deletedInitialRecords,
      newAddedInitialRecords,
    });

    const pendingChangesCount = pendingChangesCounts[id];

    acc.push(
      getTab({
        applicationId,
        tab,
        tablePath,
        actionType,
        recordId: id,
        borrowerId,
        label: (
          <TabLabel
            hasErrors={!!validationErrorSummary[id]}
            id={id}
            isDeleted={isDeletedByCurrentSession}
            label={getNavigationLabelText({ tablePath, tabData })}
            pendingChangesCount={pendingChangesCount}
            tablePath={tablePath}
            withButton={!isDeletedByInit && !isSubjectProperty}
            onDeleteRecord={onDeleteRecord}
            onRestoreRecord={onRestoreRecord}
          />
        ),
        pathFrom,
        value: id,
        history,
        isDeleted: isDeletedByInit,
        setIsNestedEntityEditActive,
        isNestedEntityEditActive,
        dispatch,
      }),
    );

    return acc;
  }, []);
